import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { nablyudatStranitsu, otmetitDeystvie, otpravitProfil } from '../auth/browserObservation';
import { flush, kanonAdresa, nazvatTekushchiyRoute, track, vkljuchit } from './tracker';

/** Экран с его таймингом: сколько был открыт и сколько из этого шла работа. */
interface Vid {
  pathname: string;
  start: number;
  /** Накопленное активное время, мс (`s698`). */
  active: number;
  /** Последний такт счётчика активности. */
  tick: number;
  otpravlen: boolean;
}

/** Без действий дольше этого — человек отошёл: время идёт вкладке, а не работе. */
const PROSTOY_MS = 60_000;

/**
 * Слой журнала ходьбы — живёт НАД маршрутами (как телефон оператора и Стол):
 * каждый переход по панели проходит через него, и НОВЫЙ ЭКРАН ПОЛУЧАЕТ ЗАМЕР
 * АВТОМАТИЧЕСКИ, ничего не подключая, — это критерий приёмки `zhur1`.
 * Ловушка полноты (`hodba-polnota.test.ts`) стережёт, что <Routes> не выехал
 * из-под слоя.
 *
 * page_view пишется при УХОДЕ с экрана — так у события есть честный тайминг
 * «сколько пробыли». Уход ловится сменой адреса и сворачиванием вкладки;
 * первый экран сессии пишется при входе следующего или сворачивании.
 *
 * ⛔ АКТИВНОЕ ВРЕМЯ — НЕ ВРЕМЯ ОТКРЫТОЙ ВКЛАДКИ (`s698`, слова владельца: «фоновую
 * вкладку не считать работой»). Секунда идёт в `active_ms`, только если вкладка
 * видна, окно в фокусе и человек что-то делал в последнюю минуту. Что именно он
 * делал, слой не читает: ему нужен сам факт настоящего (isTrusted) нажатия,
 * клавиши или прокрутки — без клавиш, текста и координат.
 */
export function HodbaSloy({ children }: { children: ReactNode }) {
  const location = useLocation();
  const tekushchiy = useRef<Vid | null>(null);

  useEffect(() => {
    vkljuchit();
    void otpravitProfil();

    let posledneeDeystvie = -Infinity;
    const deystvie = (e: Event) => {
      if (!e.isTrusted) return;
      posledneeDeystvie = performance.now();
      // Тот же момент — серверу, для «сколько прошло с последней активности на экране».
      otmetitDeystvie();
    };
    const SOBYTIYA = ['pointerdown', 'keydown', 'scroll', 'wheel', 'touchstart'] as const;
    for (const s of SOBYTIYA) window.addEventListener(s, deystvie, { passive: true, capture: true });

    const takt = window.setInterval(() => {
      const view = tekushchiy.current;
      const now = performance.now();
      if (!view || view.otpravlen) return;
      const rabotaet = document.visibilityState === 'visible' && document.hasFocus() && now - posledneeDeystvie < PROSTOY_MS;
      // Не больше секунды за такт: уснувшая вкладка не должна «доработать» задним числом.
      if (rabotaet) view.active += Math.min(1000, now - view.tick);
      view.tick = now;
    }, 1000);

    const otpravit = (view: Vid) => {
      if (view.otpravlen) return;
      view.otpravlen = true;

      const { route, entity_type, entity_id } = kanonAdresa(view.pathname);
      track({
        kind: 'page_view',
        route,
        entity_type,
        entity_id,
        active_ms: Math.round(view.active),
        duration_ms: Math.max(0, Math.round(performance.now() - view.start)),
      });
    };

    const priSkrytii = () => {
      if (document.visibilityState === 'hidden' && tekushchiy.current) {
        otpravit(tekushchiy.current);
        // Свой flush после записи: слушатели трекера могли отработать раньше
        // этого, и последний экран остался бы в очереди навсегда.
        void flush();
      }
    };

    document.addEventListener('visibilitychange', priSkrytii);
    window.addEventListener('pagehide', priSkrytii);

    return () => {
      window.clearInterval(takt);
      for (const s of SOBYTIYA) window.removeEventListener(s, deystvie, { capture: true });
      document.removeEventListener('visibilitychange', priSkrytii);
      window.removeEventListener('pagehide', priSkrytii);
    };
  }, []);

  useEffect(() => {
    // Уход со старого экрана — его page_view с таймингом.
    if (tekushchiy.current && tekushchiy.current.pathname !== location.pathname && !tekushchiy.current.otpravlen) {
      const view = tekushchiy.current;
      view.otpravlen = true;

      const { route, entity_type, entity_id } = kanonAdresa(view.pathname);
      track({
        kind: 'page_view',
        route,
        entity_type,
        entity_id,
        active_ms: Math.round(view.active),
        duration_ms: Math.max(0, Math.round(performance.now() - view.start)),
      });
    }

    const now = performance.now();
    tekushchiy.current = { pathname: location.pathname, start: now, active: 0, tick: now, otpravlen: false };
    nazvatTekushchiyRoute(kanonAdresa(location.pathname).route);
  }, [location.pathname]);

  // Страница входа: «открыта» и «тронута рукой» — до входа человека ещё нет, есть
  // только посетитель. Приглашение сообщает о себе само (`AcceptInvitePage`): ему
  // сначала нужен ответ сервера, что ссылка жива.
  useEffect(() => {
    if (location.pathname !== '/login') return;
    return nablyudatStranitsu('login');
  }, [location.pathname]);

  return <>{children}</>;
}
